import { useCallback, useRef, useState } from 'react';

import type { ImMNElement } from '../../../../../../common/elements/elementModelTypes';

type EditorFootprint = {
    width: number;
    height: number;
};

type UseImageEditorSaveFreezeOptions = {
    // Only the V2 editor freezes on save. V1 clears the editor and reloads from scratch, so it never
    // needs to hold a stale view and should pass `enabled: false`.
    enabled: boolean;
    // The live editor inputs. While frozen these are ignored in favour of the snapshot taken at save time.
    element: ImMNElement;
    editorImageLoadKey: string;
    largestUneditedImageUrl: string | undefined;
};

type UseImageEditorSaveFreeze = {
    // True once a save has started while freezing is enabled. See the note below on why it never resets.
    isFrozen: boolean;
    // Call at the moment a save begins to snapshot the editor's current view and box size.
    onSaveStart: (editorFootprint: EditorFootprint) => void;
    // The element + image-load inputs the editor should render. While frozen these are the snapshot, not
    // the live (and now changing) props.
    displayElement: ImMNElement;
    activeEditorImageLoadKey: string;
    activeLargestUneditedImageUrl: string | undefined;
    frozenEditorFootprint: EditorFootprint | null;
};

/**
 * Freeze what the V2 image editor renders while a save is in flight.
 *
 * Why this is needed: saving an edit dispatches Redux updates that immediately mutate the `element`
 * prop — a new crop variant, a rotated/flipped source, a background-removal toggle. If the editor kept
 * reacting to those changes mid-save it would re-initialise its rotation/scale state, recompute its
 * image-load key, and tear down + reload the Cropper. The user would see the image flash or jump while
 * the modal is already closing or the new render is still uploading.
 *
 * To avoid that, the first `onSaveStart` snapshots the element and image-load inputs into refs (they are
 * written once and only read afterwards, so they must not cause a re-render) and latches `isFrozen` on.
 * From then on the editor renders the snapshot instead of the live prop, holding a stable picture until
 * it goes away.
 *
 * The latch is intentionally one-way — there is no reset. A save always ends with the editor either
 * unmounting (the modal closes) or fully remounting (the element id changes when re-opened), so this hook
 * starts again from a clean `isFrozen: false`. Adding a reset path would risk un-freezing mid-save and
 * reintroducing exactly the flash this exists to prevent.
 */
export const useImageEditorSaveFreeze = ({
    enabled,
    element,
    editorImageLoadKey,
    largestUneditedImageUrl,
}: UseImageEditorSaveFreezeOptions): UseImageEditorSaveFreeze => {
    const [isSaving, setIsSaving] = useState(false);

    const savingElementRef = useRef<ImMNElement | null>(null);
    const savingEditorImageLoadKeyRef = useRef<string | null>(null);
    const savingLargestUneditedImageUrlRef = useRef<string | undefined>();
    const savingEditorFootprintRef = useRef<EditorFootprint | null>(null);

    const isFrozen = enabled && isSaving;

    const onSaveStart = useCallback(
        (editorFootprint: EditorFootprint) => {
            if (!enabled) return;

            savingElementRef.current = element;
            savingEditorImageLoadKeyRef.current = editorImageLoadKey;
            savingLargestUneditedImageUrlRef.current = largestUneditedImageUrl;
            savingEditorFootprintRef.current = editorFootprint;
            setIsSaving(true);
        },
        [enabled, element, editorImageLoadKey, largestUneditedImageUrl],
    );

    return {
        isFrozen,
        onSaveStart,
        // While frozen, prefer the snapshot. The `|| live` fallbacks only cover the impossible-but-cheap
        // case of reading a ref before the first snapshot is written.
        displayElement: isFrozen ? savingElementRef.current || element : element,
        activeEditorImageLoadKey: isFrozen
            ? savingEditorImageLoadKeyRef.current || editorImageLoadKey
            : editorImageLoadKey,
        activeLargestUneditedImageUrl: isFrozen
            ? savingLargestUneditedImageUrlRef.current || largestUneditedImageUrl
            : largestUneditedImageUrl,
        frozenEditorFootprint: isFrozen ? savingEditorFootprintRef.current : null,
    };
};
